import { computed, type Ref } from 'vue';
import type { ICsvData } from '@/interfaces/storageTracking/ICsvData';
import type { IEnactPlants, IPlantDetails, IPlantDetailsPlusRelated } from '@/interfaces/powerMarkets/plant/IPlantDetails';
import cssVariables from '@/assets/cssVariables.json';
import { IMapDataItem } from '@/interfaces/powerMarkets/maps/IMapDataItem.ts';
import { formatNumberToMaxDecimalPlaces } from '@/utils/numberUtils.ts';

interface UsePlantDetailsReturn {
    technologyOrFuelValue: Ref<string[]>;
    plantIsStorage: Ref<boolean>;
    isMultipleBmUnitsView: Ref<boolean>;
    decommissioningStatus: Ref<string>;
}

interface UseMultiPlantDetailsReturn {
    plantMapData: Ref<Array<IMapDataItem>>;
}

export function getHeaderProperties (
    technology: string[],
    genCapacity: number | null | undefined,
    storageCapacity: number | null | undefined,
    demCapacity: number | null | undefined,
    isStorage: boolean,
    isParentPlant: boolean,
    showBmuData: boolean = false,
    isMultipleBmu: boolean = false,
    decommissioningStatus: string | null = null,
    lastZero: string | null = null,
    zone: string | null = null,
    extraInfo: string | null = null,
): Record<string, string | number> {
    const headerProps = {} as Record<string, string | number>;

    if (isParentPlant) {
        headerProps.Technology = technology.join(', ');
    }

    if (isParentPlant || isMultipleBmu) {
        headerProps.Power = genCapacity ? `${formatNumberToMaxDecimalPlaces(genCapacity)} MW` : '-';

        if (isStorage) {
            headerProps.Energy = storageCapacity ? `${formatNumberToMaxDecimalPlaces(storageCapacity)} MWh` : '-';

            const duration = storageCapacity && genCapacity ? storageCapacity / genCapacity : null;
            headerProps.Duration = duration ? `${formatNumberToMaxDecimalPlaces(duration)} h` : '-';
        }
    }

    if (!isStorage && demCapacity) {
        headerProps['Power (-ve)'] = demCapacity ? `${formatNumberToMaxDecimalPlaces(demCapacity)} MW` : '-';
    }

    if (showBmuData) {
        if (decommissioningStatus) headerProps.Status = decommissioningStatus;
        if (lastZero) headerProps['Last Time Off'] = lastZero;
        if (zone) headerProps.Zone = zone;
        if (extraInfo && extraInfo !== 'None') {
            headerProps.Info = extraInfo;
        }
    }

    return headerProps;
}

export function usePlantDetails (
    plantInfo: IPlantDetails | null,
    relatedPlantInfo: IEnactPlants | null = null,
    relatedStoretrackDetails: ICsvData | null = null,
): UsePlantDetailsReturn {
    const technologyOrFuelValue = computed<string[]>(() => {
        if (relatedStoretrackDetails) return relatedStoretrackDetails.technology;
        return plantInfo ? [plantInfo.fuel] : ['-'];
    });

    const plantIsStorage = computed<boolean>(() => {
        if (relatedStoretrackDetails) return true;
        if (plantInfo) return plantInfo.fuel === 'Battery';
        return false;
    });

    const isMultipleBmUnitsView = computed(() => (relatedPlantInfo ? relatedPlantInfo.plants.length > 1 : false));

    const decommissioningStatus = computed<string>(() => {
        if (plantInfo?.decommissioned) return 'Decommissioned';
        return 'Active';
    });

    return {
        technologyOrFuelValue,
        plantIsStorage,
        isMultipleBmUnitsView,
        decommissioningStatus,
    };
}

export function useMultiPlantDetails (
    plants: Record<string, IPlantDetailsPlusRelated>,
): UseMultiPlantDetailsReturn {
    const plantMapData = computed<Array<IMapDataItem>>(() => Object.entries(plants).flatMap(([plantId, plantDetails]) => {
        if (plantDetails.relatedEnactData?.plants && plantDetails.relatedEnactData.plants.length > 0) {
            return plantDetails.relatedEnactData.plants.map(element => ({
                id: plantId,
                colour: element.colour,
                mapSize: [30, 30],
                coords: element.coords === null ? [0, 0] : [...element.coords].reverse() as [number, number],
                fuelColour: null,
                size: null,
                flag: false,
                filterData: null,
                zone: element.zone,
                name: element.name,
            })) as Array<IMapDataItem>;
        }
        if (plantDetails.relatedStoretrackDetails) {
            const storetrack: ICsvData = plantDetails.relatedStoretrackDetails;
            const coords = !storetrack || storetrack.latitude == null || storetrack.longitude == null
                ? [0, 0]
                : [storetrack.latitude, storetrack.longitude];

            return [{
                id: storetrack.projectName,
                colour: cssVariables.lcpColors.lcpWatermelon,
                mapSize: [30, 30],
                coords: coords as [number, number],
                fuelColour: null,
                size: null,
                flag: false,
                filterData: null,
                zone: '',
                name: '',
            }] as Array<IMapDataItem>;
        }
        return [] as Array<IMapDataItem>;
    }));

    return {
        plantMapData,
    };
}
